import React, { useState } from "react";
import { api, euro, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import MoneyInput from "./MoneyInput";

export default function SyncProviderPanel({ providers, wallets, onDone }) {
    const [openProv, setOpenProv] = useState(null);
    const [form, setForm] = useState({ residuo: "", wallet_cash_id: "" });
    const [saving, setSaving] = useState(false);

    const cashWallets = wallets.filter(
        (w) => w.type === "cassa" || w.type === "cassaforte",
    );

    const openSync = (p) => {
        setOpenProv(p);
        setForm({
            residuo: "",
            wallet_cash_id: cashWallets[0]?.id || "",
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!openProv) return;
        setSaving(true);
        try {
            const r = await api.post("/providers/sync-residuo", {
                provider_id: openProv.id,
                residuo: parseFloat(form.residuo),
                wallet_cash_id: form.wallet_cash_id || null,
            });
            const { delta, tipo } = r.data;
            if (Math.abs(delta) < 0.01) {
                toast.info("Residuo già allineato");
            } else if (tipo === "vendita_credito") {
                toast.success(`${euro(delta)} di vendita credito in cassa`);
            } else if (tipo === "vincita_pagata") {
                toast.success(
                    `Vincita pagata di ${euro(Math.abs(delta))} dalla cassa`,
                );
            }
            setOpenProv(null);
            onDone && onDone();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (!providers || providers.length === 0) return null;

    return (
        <div className="bg-white border border-zinc-200">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
                <div className="overline flex items-center gap-2">
                    <ArrowsClockwise size={12} weight="bold" />
                    Sync rapido fornitori
                </div>
                <div className="text-xs text-zinc-500">
                    Allinea il residuo DB con quello del portale operatore
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-zinc-100">
                {providers.map((p) => {
                    const isPrepagato = (p.fido_limit || 0) === 0;
                    const residuo =
                        (p.fido_limit || 0) - (p.outstanding_credit || 0);
                    const pct =
                        p.fido_limit > 0
                            ? (p.outstanding_credit / p.fido_limit) * 100
                            : 0;
                    return (
                        <div
                            key={p.id}
                            className="p-4 flex items-center justify-between gap-3"
                            data-testid={`sync-card-${p.id}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-bold truncate flex items-center gap-2">
                                    {p.name}
                                    {isPrepagato && (
                                        <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[9px] font-bold uppercase tracking-wider border border-blue-200">
                                            prepagato
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-zinc-500 tabular-nums">
                                    {isPrepagato ? (
                                        <>Saldo: {euro(-p.outstanding_credit)}</>
                                    ) : (
                                        <>Residuo DB: {euro(residuo)} / {euro(p.fido_limit)}</>
                                    )}
                                </div>
                                {!isPrepagato && (
                                    <div className="h-1 bg-zinc-100 mt-2 relative">
                                        <div
                                            className="h-full absolute left-0 top-0"
                                            style={{
                                                width: `${Math.min(100, pct)}%`,
                                                background:
                                                    pct >= 85
                                                        ? "#FF2A00"
                                                        : pct >= 50
                                                          ? "#F59E0B"
                                                          : "#00C853",
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => openSync(p)}
                                className="inline-flex items-center justify-center w-9 h-9 border border-ink hover:bg-ink hover:text-white transition-colors"
                                data-testid={`sync-btn-${p.id}`}
                                aria-label={`Sync ${p.name}`}
                                title={`Sync residuo ${p.name}`}
                            >
                                <ArrowsClockwise size={14} weight="bold" />
                            </button>
                        </div>
                    );
                })}
            </div>

            {openProv && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setOpenProv(null)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={submit}
                        className="w-full max-w-md bg-white border border-zinc-200 p-8"
                        data-testid="sync-form"
                    >
                        <div className="overline mb-3">Sync residuo</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-2">
                            {openProv.name}
                        </h2>
                        <div className="text-xs text-zinc-600 mb-6 tabular-nums">
                            DB: residuo <strong>{euro(openProv.fido_limit - openProv.outstanding_credit)}</strong> su
                            fido {euro(openProv.fido_limit)}
                        </div>
                        <label className="block mb-4">
                            <div className="overline mb-2">Residuo sul portale €</div>
                            <MoneyInput
                                className="inp-huge tabular-nums"
                                autoFocus
                                required
                                value={form.residuo}
                                onChange={(v) =>
                                    setForm({ ...form, residuo: v })
                                }
                                placeholder="0,00"
                                data-testid="sync-form-residuo"
                            />
                        </label>
                        <label className="block mb-6">
                            <div className="overline mb-2">
                                Wallet cassa (per eventuale vendita credito)
                            </div>
                            <select
                                className="inp"
                                value={form.wallet_cash_id}
                                onChange={(e) =>
                                    setForm({ ...form, wallet_cash_id: e.target.value })
                                }
                                data-testid="sync-form-wallet"
                            >
                                <option value="">Auto (prima cassa disponibile)</option>
                                {cashWallets.map((w) => (
                                    <option key={w.id} value={w.id}>
                                        {w.name} ({euro(w.balance)})
                                    </option>
                                ))}
                            </select>
                        </label>
                        <div className="mb-6 p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-700">
                            Se il portale ha residuo <strong>MINORE</strong> del DB: vendita
                            credito (cassa +, outstanding +). Se <strong>MAGGIORE</strong>:
                            vincita pagata al cliente (cassa −, outstanding −).
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setOpenProv(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="btn-primary flex-1"
                                data-testid="sync-form-submit"
                            >
                                {saving ? "Sync…" : "Sincronizza"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
